import { ResultOf } from "@graphql-typed-document-node/core";
import { Label } from "@radix-ui/react-label";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Info } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { CopyToClipboardButton } from "@/components/CopyToClipboardButton";
import { RoundEditDialog } from "@/components/dialogs/RoundEditDialog";
import { TrialCreateDialog } from "@/components/dialogs/TrialCreateDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getFragmentData, graphql } from "@/gen";
import { extractChessresultsTournamentId } from "@/utils/chessresults";
import { getPgnProviderDisplayName } from "@/utils/pgn-provider";

import {
    RoundT,
    importChessresultsPairingsForRoundMutation,
    recalculateRoundTrialResultsMutation,
    setAllGamesScheduledStartAtToRoundMutation,
} from "./queries";

export default function RoundRoute() {
    const { round } = useOutletContext<{ round: RoundT }>();
    const [chessresultsId, setChessresultsId] = useState("");
    const [roundNo, setRoundNo] = useState<number | "">("");
    const [_importPairingsResult, importPairings] = useMutation(
        importChessresultsPairingsForRoundMutation
    );
    const [_setStartAtResult, setAllGamesScheduledStartAtToRound] = useMutation(
        setAllGamesScheduledStartAtToRoundMutation
    );
    const [_recalculateResult, recalculateTrialResults] = useMutation(
        recalculateRoundTrialResultsMutation
    );

    const handleImport = async () => {
        const extractedId = extractChessresultsTournamentId(chessresultsId);

        if (!extractedId || roundNo === "" || roundNo <= 0) {
            toast.error("Please enter a Chessresults URL and a round number");
            return;
        }

        const res = await importPairings({
            input: {
                roundId: round.id,
                chessresultsTournamentId: extractedId.trim(),
                roundNumber: Number(roundNo),
            },
        });

        if (res.error) {
            toast.error("Import failed", { description: res.error.message });
        } else {
            toast.success("Pairings imported.");
        }
    };

    const setStartAtTimeForGames = async () => {
        const confirmed = window.confirm(
            `This will update the scheduled start time for ALL games in this round to match the round's scheduled start time. Are you sure?`
        );
        if (!confirmed) return;

        const res = await setAllGamesScheduledStartAtToRound({
            roundId: round.id,
        });

        if (res.error) {
            toast.error("Failed to set scheduled start at time for games", {
                description: res.error.message,
            });
        } else {
            toast.success("Updated scheduled start at time for games");
        }
    };

    const handleRecalculateTrialResults = async () => {
        const confirmed = window.confirm(
            `This will recalculate trial results for all trials in this round that have games with results but no trial result. Are you sure?`
        );
        if (!confirmed) return;

        const res = await recalculateTrialResults({
            roundId: round.id,
        });

        if (res.error) {
            toast.error("Failed to recalculate trial results", {
                description: res.error.message,
            });
        } else if (res.data?.recalculateRoundTrialResults) {
            const { totalTrialsChecked, trialsUpdated, trialsFailed } =
                res.data.recalculateRoundTrialResults;
            toast.success(`Recalculation complete`, {
                description: `Checked: ${totalTrialsChecked}, Updated: ${trialsUpdated}, Failed: ${trialsFailed}`,
            });
        }
    };

    return (
        <Card className="bg-brand-white text-brand-black m-4 rounded border-none">
            <CardHeader>
                <CardTitle>{round.title}</CardTitle>
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <div className="flex-row items-center">
                            <Label className="text-sm font-medium">ID: </Label>
                            <>
                                {round.id}
                                <CopyToClipboardButton text={round.id} />
                            </>
                        </div>
                    </div>
                    <RoundEditDialog />
                </div>
            </CardHeader>
            <CardContent>
                <div className={"flex flex-col gap-4"}>
                    <div>
                        <Label className="text-md font-medium">Title</Label>
                        <p className="text-md text-muted-foreground">{round.title || "—"}</p>
                    </div>
                    <div>
                        <Label className="text-md font-medium">Scheduled start at</Label>
                        <p className="text-md text-muted-foreground">
                            {format(round.scheduledStartAt, "PPpp") || "—"}
                        </p>

                        <div className="mt-2 flex items-center gap-2">
                            <Button onClick={setStartAtTimeForGames} variant="outline" size="sm">
                                Sync games to round start
                            </Button>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    This sets the scheduled start time for every game in this round
                                    to match the round's scheduled start time.
                                </TooltipContent>
                            </Tooltip>
                        </div>

                        <div className="mt-2 flex items-center gap-2">
                            <Button
                                onClick={handleRecalculateTrialResults}
                                variant="outline"
                                size="sm"
                            >
                                Recalculate trial results
                            </Button>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    This recalculates trial results for all trials in this round
                                    that have games with results but no trial result.
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>
                    <div>
                        <Label className="text-md font-medium">Last game scheduled start at</Label>
                        <p className="text-md text-muted-foreground">
                            {round.lastGameScheduledStartAt
                                ? format(round.lastGameScheduledStartAt, "PPpp")
                                : "—"}
                        </p>
                    </div>

                    <div className="my-4 flex flex-col gap-2 rounded border bg-gray-50 p-4">
                        <Label className="mb-2 block text-lg font-semibold">Syncer Config</Label>

                        {round.roundSyncerConfig == null ? (
                            <h1>No syncer config</h1>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <Label className="block text-sm font-semibold">
                                    PGN provider type:
                                </Label>
                                <p className="text-sm">
                                    {getPgnProviderDisplayName(
                                        round.roundSyncerConfig.pgnProviderType
                                    )}
                                </p>
                                {/* POLL_PGN specific fields */}
                                {round.roundSyncerConfig.pgnProviderType === "POLL_PGN" &&
                                    round.roundSyncerConfig.pollPgnSource && (
                                        <div>
                                            <Label className="block text-sm font-semibold">
                                                Poll PGN Source:
                                            </Label>
                                            <p className="text-sm">
                                                {round.roundSyncerConfig.pollPgnSource}
                                            </p>
                                        </div>
                                    )}

                                {/* STREAMING_PGN specific fields */}
                                {round.roundSyncerConfig.pgnProviderType === "STREAMING_PGN" &&
                                    round.roundSyncerConfig.streamingPgnSource && (
                                        <div>
                                            <Label className="block text-sm font-semibold">
                                                Streaming PGN Source:
                                            </Label>
                                            <p className="text-sm">
                                                {round.roundSyncerConfig.streamingPgnSource}
                                            </p>
                                        </div>
                                    )}

                                {/* LIVECHESS_CLOUD_ROUND specific fields */}
                                {round.roundSyncerConfig.pgnProviderType ===
                                    "LIVECHESS_CLOUD_ROUND" && (
                                    <>
                                        {round.roundSyncerConfig.livechesscloudRoundId && (
                                            <div>
                                                <Label className="block text-sm font-semibold">
                                                    Livechess Cloud Round id:
                                                </Label>
                                                <p className="text-sm">
                                                    {round.roundSyncerConfig.livechesscloudRoundId}
                                                </p>
                                            </div>
                                        )}
                                        {round.roundSyncerConfig.livechesscloudRoundNumber && (
                                            <div>
                                                <Label className="block text-sm font-semibold">
                                                    Livechess Cloud Round number:
                                                </Label>
                                                <p className="text-sm">
                                                    {
                                                        round.roundSyncerConfig
                                                            .livechesscloudRoundNumber
                                                    }
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Common fields for all non-MANUAL providers */}
                                {round.roundSyncerConfig.pgnProviderType !== "MANUAL" && (
                                    <>
                                        {round.roundSyncerConfig.pollStart && (
                                            <>
                                                <Label className="block text-sm font-semibold">
                                                    Poll Start:
                                                </Label>
                                                <p className="text-sm">
                                                    {format(
                                                        round.roundSyncerConfig.pollStart,
                                                        "dd/MM-yyyy HH:mm"
                                                    )}
                                                </p>
                                            </>
                                        )}
                                        {round.roundSyncerConfig.pollEnd && (
                                            <>
                                                <Label className="block text-sm font-semibold">
                                                    Poll End:
                                                </Label>
                                                <p className="text-sm">
                                                    {format(
                                                        round.roundSyncerConfig.pollEnd,
                                                        "dd/MM-yyyy HH:mm"
                                                    )}
                                                </p>
                                            </>
                                        )}
                                        {typeof round.roundSyncerConfig.pollCreateGames ===
                                            "boolean" && (
                                            <>
                                                <Label className="block text-sm font-semibold">
                                                    Poll create games:
                                                </Label>
                                                <p className="text-sm">
                                                    {round.roundSyncerConfig.pollCreateGames
                                                        ? "Yes"
                                                        : "No"}
                                                </p>
                                            </>
                                        )}
                                        <Label className="block text-sm font-semibold">
                                            PGN Delay (seconds):
                                        </Label>
                                        <p className="text-sm">
                                            {round.roundSyncerConfig.pgnDelaySecs ?? 0}
                                        </p>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="my-4 rounded border bg-gray-50 p-4">
                        <Label className="mb-2 block text-lg font-semibold">
                            Import pairings from Chessresults
                        </Label>

                        <div className="flex flex-col gap-2 md:flex-row md:items-center">
                            <Input
                                className="flex-1"
                                placeholder="Chessresults tournament ID or URL"
                                value={chessresultsId}
                                onChange={e => setChessresultsId(e.target.value)}
                            />

                            <Input
                                type="number"
                                min={1}
                                className="w-32"
                                placeholder="Round #"
                                value={roundNo}
                                onChange={e =>
                                    setRoundNo(e.target.value ? Number(e.target.value) : "")
                                }
                            />

                            <Button onClick={handleImport}>Import pairings</Button>
                        </div>
                    </div>

                    <TrialTable roundId={round.id} />
                </div>
            </CardContent>
        </Card>
    );
}

const usePaginatedTrials_Trial = graphql(`
    fragment usePaginatedTrials_Trial on Trial {
        id
        finishedAt
        player1 {
            id
            name
            nickname
        }
        player2 {
            id
            name
            nickname
        }
        games {
            id
        }
        createdAt
        result
    }
`);

type TrialRowT = ResultOf<typeof usePaginatedTrials_Trial>;

const columns: ColumnDef<TrialRowT>[] = [
    {
        accessorKey: "player1",
        header: () => <div>Player 1</div>,
        cell: ({ row }) => {
            return <div>{row.original.player1.nickname}</div>;
        },
    },
    {
        accessorKey: "player2",
        header: () => <div>Player 2</div>,
        cell: ({ row }) => {
            return <div>{row.original.player2.nickname}</div>;
        },
    },
    {
        accessorKey: "gamesCount",
        header: () => <div># games</div>,
        cell: ({ row }) => {
            return <div>{row.original.games.length}</div>;
        },
    },
    {
        accessorKey: "finishedAt",
        header: () => <div>Finished at</div>,
        cell: ({ row }) => {
            return (
                <div className="font-medium">
                    {row.original.finishedAt ? format(row.original.finishedAt, "PPpp") : "-"}
                </div>
            );
        },
    },
    {
        accessorKey: "result",
        header: () => <div>Result</div>,
        cell: ({ row }) => {
            return <div>{row.original.result}</div>;
        },
    },
];

const trialsQuery = graphql(`
    query trialsQuery($roundId: UUID!, $limit: Int!, $offset: Int!) {
        trials(
            input: {
                filter: { withRoundId: $roundId }
                pagination: { limit: $limit, offset: $offset }
            }
        ) {
            items {
                id
                ...usePaginatedTrials_Trial
            }
            total
        }
    }
`);

const TrialTable = ({ roundId }: { roundId: string }) => {
    const navigate = useNavigate();
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);

    const [{ data, fetching }, refetch] = useQuery({
        query: trialsQuery,
        variables: {
            roundId,
            limit,
            offset,
        },
    });

    const trials = useMemo(
        () => getFragmentData(usePaginatedTrials_Trial, data?.trials.items ?? []),
        [data?.trials.items]
    );

    return (
        <div>
            {trials.length === 0 && fetching ? (
                <div>Loading</div>
            ) : (
                <DataTable
                    columns={columns}
                    data={trials}
                    onRowClick={row => {
                        navigate(`trials/${row.id}`);
                    }}
                    title="Trials"
                    addButton={<TrialCreateDialog onCreated={refetch} />}
                    pagination={{
                        limit,
                        offset,
                        setLimit,
                        setOffset,
                        total: data?.trials.total ?? 0,
                    }}
                />
            )}
        </div>
    );
};
